import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';

interface FilingTypeDetectionProps {
  brand: BrandConfig;
  filingType: string;
  caseNumber: string;
  onSwitchFilingType: (newType: 'case-initiation' | 'subsequent-filing') => void;
  /**
   * Confirm flow only (WOL-443): a manual "Existing case" pick has no detected
   * case number, so render the generic existing-case line instead of
   * interpolating an empty number. Classic keeps its exact current output.
   */
  confirmFlow?: boolean;
}

const FilingTypeDetection: React.FC<FilingTypeDetectionProps> = ({
  brand,
  filingType,
  caseNumber,
  onSwitchFilingType,
  confirmFlow,
}) => {
  const isSubsequent = filingType === 'subsequent-filing';

  // Unset filing type (extraction failed or never ran — applyExtraction
  // defaults it when it runs, though in the confirm flow never over an
  // existing choice, R88): ask, don't assert. The old rendering claimed
  // "This looks like a new case" while nothing was set, and the only button
  // switched to subsequent — the filer had to leave the step to affirmatively
  // pick Case Initiation. An explicit in-step choice keeps the document-typing
  // chain completable here, strictly forward (Walter 2026-07-10 R14–R16).
  if (!filingType) {
    return (
      <div
        data-testid="filing-type-detection"
        className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg mb-6"
        style={{
          backgroundColor: brand.colors.bgLight,
          border: `1px solid ${brand.colors.border}`,
        }}
      >
        <div className="flex items-center gap-3">
          <span className="text-lg" role="img" aria-hidden="true">
            {'📄'}
          </span>
          <span className="text-sm font-medium" style={{ color: brand.colors.text }}>
            Is this starting a new case, or filing into an existing case?
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onSwitchFilingType('case-initiation')}
            className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors hover:opacity-80"
            style={{
              backgroundColor: `${brand.colors.primary}10`,
              color: brand.colors.link,
            }}
          >
            New case
          </button>
          <button
            onClick={() => onSwitchFilingType('subsequent-filing')}
            className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors hover:opacity-80"
            style={{
              backgroundColor: `${brand.colors.primary}10`,
              color: brand.colors.link,
            }}
          >
            Existing case
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="filing-type-detection"
      className="flex items-center justify-between p-4 rounded-lg mb-6"
      style={{
        backgroundColor: brand.colors.bgLight,
        border: `1px solid ${brand.colors.border}`,
      }}
    >
      <div className="flex items-center gap-3">
        <span
          className="text-lg"
          role="img"
          aria-hidden="true"
        >
          {isSubsequent ? '📂' : '📄'}
        </span>
        <span
          className="text-sm font-medium"
          style={{ color: brand.colors.text }}
        >
          {isSubsequent
            ? (!caseNumber
                ? 'This is a filing into an existing case'
                : `This is a filing for case ${caseNumber}`)
            : 'This looks like a new case'}
        </span>
      </div>

      <button
        onClick={() =>
          onSwitchFilingType(isSubsequent ? 'case-initiation' : 'subsequent-filing')
        }
        className="text-xs font-medium transition-colors hover:opacity-80"
        style={{ color: brand.colors.link }}
      >
        {isSubsequent
          ? 'Start a new case instead →'
          : "This isn't a new case →"}
      </button>
    </div>
  );
};

export default FilingTypeDetection;
